import { parseISO } from "date-fns";
import { ArrowLeft, Copy, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui";
import type { EvidenceDetailTab, EvidenceDetailUI } from "~/features/evidences/types";
import { cn } from "~/lib/utils";
import { EvidenceDataTab } from "./EvidenceDataTab";
import { EvidenceVersionHistoryTab } from "./EvidenceVersionHistoryTab";

interface EvidenceDetailPresenterProps {
  detail: EvidenceDetailUI;
  onBack: () => void;
  onCopyId: (id: string) => void;
  onRequestToggleActivation: () => void;
  activeTab: EvidenceDetailTab;
  onTabChange: (next: EvidenceDetailTab) => void;
  /** Version row to highlight in the version history. */
  selectedVersionId: string | null;
  /** "page" = full-page route (own width + back button); "peek" = inside the Sheet. */
  variant?: "page" | "peek";
}

export function EvidenceDetailPresenter({
  detail,
  onBack,
  onCopyId,
  onRequestToggleActivation,
  activeTab,
  onTabChange,
  selectedVersionId,
  variant = "page",
}: EvidenceDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {!isPeek && (
            <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
              <ArrowLeft />
              <span>{t("ui.back")}</span>
            </Button>
          )}
          <div className="flex items-center gap-2">
            <h1 className={cn("text-fg-1", isPeek ? "text-16-bold" : "text-dewey-title")}>{detail.name}</h1>
            {detail.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
          </div>
          <div className="flex items-center gap-2 text-12-regular text-fg-3">
            <span className="tabular-nums">{detail.id}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              aria-label={t("ui.copyId")}
              onClick={() => onCopyId(detail.id)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-4 text-12-regular text-fg-3">
            {detail.createdAt && (
              <span>
                {t("model.common.createdAt")}: {t("date.long", { date: parseISO(detail.createdAt) })}
              </span>
            )}
            {detail.updatedAt && (
              <span>
                {t("model.common.updatedAt")}: {t("date.long", { date: parseISO(detail.updatedAt) })}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {detail.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onRequestToggleActivation}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onRequestToggleActivation}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => onTabChange(value as EvidenceDetailTab)}>
        <TabsList>
          <TabsTrigger value="data">{t("ui.data")}</TabsTrigger>
          <TabsTrigger value="lineage">{t("ui.versionHistory")}</TabsTrigger>
        </TabsList>
        <TabsContent value="data" className="pt-4">
          <EvidenceDataTab detail={detail} />
        </TabsContent>
        <TabsContent value="lineage" className="pt-4">
          <EvidenceVersionHistoryTab
            headVersion={detail.headVersion}
            versions={detail.versions}
            selectedVersionId={selectedVersionId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
